import { Button } from '@almbrand/button';
import { Container } from '@almbrand/container';
import { Headline } from '@almbrand/headline';
import classNames from 'classnames';
import navigationStyles from '../../1-atoms/ButtonNavigationComponent/ButtonNavigationComponent.module.scss';
import { ModalComponent } from 'components/1-atoms/ModalComponent';
import ctaStyles from '../../2-molecules/CtaGroupComponent/CtaComponent.module.scss';
import { CtaGroupComponentProps } from 'components/2-molecules/CtaGroupComponent/CtaGroupComponent';
import { Component } from 'components/Component';
import { PageContext } from 'context/PageContext';
import { useModal, useNavigation, usePageParam } from 'hooks';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { useAuthStatus, useLogin } from '../../../features/auth';
import { getActiveCustomerModal } from '../../../hooks/useCustomerModal';
import { CUSTOMER_HAS_POLICIES } from '../../../services/CustomerService';
import { getComponentProperties } from '../../../services/MapFieldService';
import { getReplacedValue } from '../../../services/PlaceholderService';
import { getCustomerAccess } from './customerAccess/customerAccess';
import styles from './StepAuthenticationGate.module.scss';

interface StepAuthenticationGateProps {
	stepBlock?: StepBlock;
	children: ReactNode;
}

type AuthCheckStatus = 'checking' | 'authenticated' | 'logged-out' | 'error';

/**
 * Holds the auth hooks and login prompt for a step with `requiresAuthentication`.
 * Kept separate so steps that don't need login never create this state.
 */
const ProtectedStep: React.FC<Required<StepAuthenticationGateProps>> = ({ stepBlock, children }) => {
	const { getPageParam } = usePageParam();
	const { navigate } = useNavigation();
	const { orderedPageList } = useAppSelector((state) => state.pages);
	const { displayModal } = useModal();
	const { checkStatus } = useAuthStatus();
	const { login, loading: loginLoading } = useLogin();
	const { loginPromptStep, metaArea } = useContext(PageContext) ?? {};
	// Loaded by useFetchCustomer on page load (also right after the login redirect).
	const customer = useAppSelector((state) => state.customer);
	const customerProducts = useAppSelector((state) => state.customerProducts);
	// Written by useFetchCustomer once the policies are stored; the CMS "no policies" modal depends on it.
	const hasPoliciesField = useAppSelector((state) => state.calculatorForm?.[CUSTOMER_HAS_POLICIES]);
	const modalOpen = useAppSelector((state) => state.modal.displayModal);
	// Starts as 'checking' so the protected fields are hidden on the very first render.
	const [status, setStatus] = useState<AuthCheckStatus>('checking');
	const customerModalShown = useRef(false);

	// MultiplePageTemplate keeps every visited step mounted, so only the step in `?page=` is active.
	const isActiveStep = getPageParam() === stepBlock.contentId?.toString();

	const statusError = status === 'error';
	const busy = status === 'checking' || loginLoading;
	// Logged in: the step is only shown to customers with policies.
	const customerAccess =
		isActiveStep && status === 'authenticated' ? getCustomerAccess(customer, customerProducts) : undefined;

	const blockedAccess =
		customerAccess === 'not-customer' || customerAccess === 'no-policies' ? customerAccess : undefined;

	const showLoginPrompt = isActiveStep && status !== 'authenticated';

	// The "no policies" CMS modal depends on CUSTOMER_HAS_POLICIES, so wait until it is written.
	const customerModalReady =
		blockedAccess === 'not-customer' || (blockedAccess === 'no-policies' && !!hasPoliciesField);

	// Checks the login status when the step becomes active, following React's "fetch with a cleanup flag" pattern:
	// https://react.dev/learn/synchronizing-with-effects#fetching-data
	useEffect(() => {
		// Inactive steps don't call the API. Resetting means coming back to this step
		// always starts a fresh check instead of showing an earlier authenticated result.
		if (!isActiveStep) {
			setStatus('checking');
			customerModalShown.current = false;
			return;
		}

		// Cleanup sets `isOutdated`, so a response that arrives after the user left the step
		// or the component unmounted can't overwrite the current status.
		let isOutdated = false;
		const loadStatus = async () => {
			try {
				const result = await checkStatus();
				if (!isOutdated) setStatus(result.authenticated ? 'authenticated' : 'logged-out');
			} catch {
				if (!isOutdated) setStatus('error');
			}
		};
		void loadStatus();

		return () => {
			isOutdated = true;
		};
	}, [isActiveStep, checkStatus]);

	// Not a customer / no policies: open the matching CMS customer modal once per visit to the step.
	// Without a matching CMS modal the step just stays hidden.
	useEffect(() => {
		if (!customerModalReady || customerModalShown.current) {
			return;
		}
		const cmsModal = getActiveCustomerModal(metaArea);
		if (!cmsModal) {
			return;
		}
		customerModalShown.current = true;
		displayModal({ title: cmsModal.title, subtitle: cmsModal.subtitle, content: cmsModal.content });
	}, [customerModalReady]);

	// Closing the customer modal sends the user back to the previous step, so a blocked step is never left empty.
	// On the first step there is nowhere to go back to, so it stays blocked.
	const modalWasOpen = useRef(modalOpen);
	useEffect(() => {
		const modalClosed = modalWasOpen.current && !modalOpen;
		modalWasOpen.current = modalOpen;
		if (!modalClosed || !blockedAccess || !customerModalShown.current) {
			return;
		}
		if (orderedPageList.indexOf(stepBlock.contentId) > 0) {
			navigate(stepBlock.contentId, false);
		}
	}, [modalOpen]);

	// Used by "Prøv igen" and after a login attempt that didn't leave the page.
	const recheckStatus = async () => {
		setStatus('checking');
		try {
			const result = await checkStatus();
			setStatus(result.authenticated ? 'authenticated' : 'logged-out');
		} catch {
			setStatus('error');
		}
	};

	// Login only starts from the button; entering the step never redirects automatically.
	// If login returns without leaving the page (e.g. already logged in, or an error), recheck
	// so the step can unlock or the prompt can show the latest state.
	const handleLogin = async () => {
		await login();
		await recheckStatus();
	};

	// The CTA group only supplies the login button (its forward "submit" button), so it isn't rendered as a field.
	const loginFields = loginPromptStep?.fields?.items ?? [];
	const ctaGroup = loginFields.find((item) => item.content?.contentType === 'CtaGroupBlock')?.content as
		Pick<CtaGroupComponentProps, 'ctaGroup'> | undefined;
	const loginButton = ctaGroup?.ctaGroup?.items?.find(
		(button) => button.content?.buttonActionType === 'submit'
	)?.content;
	const contentFields = loginFields.filter((item) => item.content?.contentType !== 'CtaGroupBlock');

	return (
		<>
			{customerAccess === 'has-policies' && children}
			{customerAccess === 'loading' && <p role='status'>Henter oplysninger...</p>}
			{customerAccess === 'error' && (
				<p role='alert'>Vi kunne ikke hente dine kundeoplysninger. Prøv igen senere.</p>
			)}
			{/* The step's own ModalComponent is inside the hidden children, so the gate shows the modal while blocked. */}
			{blockedAccess && modalOpen && <ModalComponent />}
			{showLoginPrompt && (
				<div>
					{/* CMS "login" step title and fields, laid out like a PageTemplate step; hard-coded text fills in whatever is missing. */}
					<Container
						mobileWidth='fullWidth'
						tabletWidth='fullWidth'
						desktopWidth='fullWidth'
					>
						<div className={styles.StepAuthenticationGate__Headline}>
							<Headline
								title={getReplacedValue(loginPromptStep?.title) || 'Log ind for at fortsætte'}
								size='default'
								variant='h3'
								fontColor='primary'
							/>
						</div>
					</Container>
					<div className={styles.StepAuthenticationGate__Content}>
						{/* Login step fields are rendered like PageTemplate does; content only, so no form props. */}
						{contentFields.length ? (
							contentFields.map((contentItem, index) => {
								const props = getComponentProperties(loginPromptStep.contentId, contentItem, index, {});
								return props ? (
									<Component
										key={contentItem.content.contentId ?? index}
										{...props}
									/>
								) : undefined;
							})
						) : (
							<p>Du skal være logget ind for at fortsætte til dette trin.</p>
						)}
						{/* Same wrappers as a step's forward CTA button, so it is spaced the same. */}
						<div
							className={classNames(
								ctaStyles.ctaComponent__navigation,
								ctaStyles.ctaComponent__navigation__single_button
							)}
						>
							<div
								className={classNames(
									navigationStyles.ButtonNavigation,
									navigationStyles.ButtonNavigation__submit
								)}
							>
								{/* Label, type and size come from the CMS forward button; the click always logs in. */}
								<Button
									type='button'
									buttonType={loginButton?.buttonType ?? 'primary'}
									size={loginButton?.size}
									disabled={busy}
									onClick={() => void (statusError ? recheckStatus() : handleLogin())}
								>
									{statusError ? 'Prøv igen' : loginButton?.label || 'Log ind'}
								</Button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

/**
 * Wraps a calculator step. Steps without `requiresAuthentication` render unchanged and skip
 * all auth work; protected steps show a login prompt until the user is logged in.
 */
export const StepAuthenticationGate: React.FC<StepAuthenticationGateProps> = ({ stepBlock, children }) =>
	stepBlock?.requiresAuthentication ? (
		<ProtectedStep stepBlock={stepBlock}>{children}</ProtectedStep>
	) : (
		<>{children}</>
	);
